/**
 * @since 2026-05-12
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 *
 * Read-only display. The reconciler auto-cleans OHLC bars (drops NaN +
 * non-positive [non-futures] + clips Low/High so Open/Close are in [Low, High])
 * so this section should always be OK in steady state. When it isn't, the
 * Acknowledge action at the section header is the only response — a non-OK
 * result means data slipped through the reconciler's safety net and warrants
 * an engineering follow-up, not a per-bar manual fix.
 */

import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";

const formatPrice = (value) => {
  if (value === null || value === undefined) return "NaN";
  if (typeof value !== "number" || !Number.isFinite(value)) return "NaN";
  if (Math.abs(value) >= 1000) return value.toFixed(2);
  if (Math.abs(value) >= 1) return value.toFixed(4);
  return value.toFixed(6);
};

const PRICE_FIELDS = [
  { key: "open", label: "O" },
  { key: "high", label: "H" },
  { key: "low", label: "L" },
  { key: "close", label: "C" },
];

const OhlcIntegrityPanel = ({ detail }) => {
  if (!detail) return null;
  const { violationTypes = [], examples = {}, frameSource, message } = detail;

  if (message) {
    return <Box sx={{ fontSize: 13, opacity: 0.7 }}>{message}</Box>;
  }

  if (violationTypes.length === 0) {
    return (
      <Box sx={{ fontSize: 13, opacity: 0.7 }}>
        No OHLC violations found
        {frameSource ? ` (evaluated on ${frameSource === "notAdjusted" ? "DfNotAdj" : "DfFullAdj"})` : ""}.
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ fontSize: 13 }}>
        <Box sx={{ opacity: 0.6, fontSize: 12, mb: 0.5 }}>
          Evaluated on {frameSource === "notAdjusted" ? "DfNotAdj" : "DfFullAdj"}.
          The reconciler auto-cleans OHLC bars — anything surfaced here slipped past it.
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {violationTypes.map((vt) => (
            <Tooltip key={vt.type} arrow title={`${vt.count} bar${vt.count === 1 ? "" : "s"} flagged`}>
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  border: "1px solid rgba(230,90,90,0.5)",
                  background: "rgba(230,90,90,0.08)",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                {vt.label}: {vt.count.toLocaleString()}
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>

      {violationTypes.map((vt) => {
        const rows = examples[vt.type] || [];
        if (rows.length === 0) return null;
        return (
          <Box key={vt.type}>
            <Typography variant="body2" sx={{ opacity: 0.75, mb: 0.5 }}>
              {vt.label} — examples ({rows.length} of {vt.count.toLocaleString()})
            </Typography>
            {rows.map((row, i) => {
              const dateOnly = (row.date || "").slice(0, 19);
              return (
                <Box
                  key={`${vt.type}-${i}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    fontSize: 13,
                    fontFamily: "monospace",
                    py: 0.25,
                  }}
                >
                  <Box component="span" sx={{ flex: "1 1 auto" }}>
                    {dateOnly}
                    {PRICE_FIELDS.map((f) => (
                      <Box component="span" key={f.key} sx={{ ml: 1.5, opacity: 0.85 }}>
                        {f.label}={formatPrice(row[f.key])}
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default OhlcIntegrityPanel;
